/**
 * useCollaboration.js
 *
 * Sets up a Yjs shared document and a y-websocket provider for a specific
 * rfpId + sectionId pair. Returns the ydoc, provider, and awareness state.
 *
 * CONNECTION POLICY
 *  - Before the first successful sync (nothing exists locally yet) we may fall
 *    back to standard editing ('unavailable') if the server cannot be reached
 *    or rejects us. That is safe because there is no shared state to lose.
 *  - AFTER the first successful sync we NEVER discard the Y.Doc. Transient
 *    drops keep retrying with backoff (y-websocket buffers local edits and
 *    re-syncs on reconnect). Permanent failures (auth/permission) stop
 *    reconnecting but keep the document bound to the editor ('error'), so
 *    unsaved work is never replaced with stale REST content. The user can
 *    call `retry()` to reconnect.
 *
 * CLEANUP: Destroys the provider and ydoc on unmount / section switch to
 * prevent memory leaks and duplicate WebSocket connections.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

// Give up on the *initial* connection (never synced) after this long.
const INITIAL_SYNC_TIMEOUT_MS = 15000;
// Initial connection attempts allowed before falling back to standard editing.
const MAX_INITIAL_FAILURES = 3;
// Close codes that mean "retrying with the same credentials will not help".
const AUTH_CLOSE_CODES = [1008, 4401, 4403];
const UNSUPPORTED_CLOSE_CODES = [1003, 1011];
// Section was submitted / locked for everyone in the room.
const LOCKED_CLOSE_CODE = 4409;

// User colors pool – each user gets a stable color derived from their userId
const USER_COLORS = [
    '#7C3AED', // violet
    '#2563EB', // blue
    '#059669', // emerald
    '#D97706', // amber
    '#DC2626', // red
    '#DB2777', // pink
    '#0891B2', // cyan
    '#65A30D', // lime
    '#9333EA', // purple
    '#EA580C', // orange
];

/**
 * Returns a stable color for a given user id string.
 */
export function getUserColor(userId) {
    if (!userId) return USER_COLORS[0];
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

/**
 * Derives user initials from a display name.
 */
export function getUserInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Reads and sanitizes the JWT used for the collab websocket handshake. */
const readToken = () => {
    let token = localStorage.getItem('access_token');
    if (token) {
        token = token.replace(/^Bearer\s+/i, '').replace(/^["']|["']$/g, '').trim();
    }
    return token || null;
};

/**
 * @param {object} params
 * @param {string} params.rfpId
 * @param {string|number} params.sectionId  – section key or id
 * @param {object} params.user              – { id, name, email }
 * @param {string} params.wsBaseUrl         – e.g. "wss://your-api.com"
 *
 * Returned `status`:
 *   'connecting' | 'connected' | 'disconnected' (auto-reconnecting) |
 *   'error' (stopped, doc kept – call retry) | 'locked' | 'unavailable' (standard editing)
 *
 * Returned `collabReady` is true once the document has synced at least once for
 * the current room (it stays true across temporary disconnects).
 */
export function useCollaboration({ rfpId, sectionId, user, wsBaseUrl, isEditing, enabled = true }) {
    const ydocRef = useRef(null);
    const providerRef = useRef(null);
    const hasSyncedRef = useRef(false);

    // Use state (not just refs) so consumers re-render when ydoc/provider become available
    const [ydoc, setYdoc] = useState(null);
    const [provider, setProvider] = useState(null);
    const [status, setStatus] = useState('connecting');
    const [collaborators, setCollaborators] = useState([]); // array of awareness user objects
    const [collabReady, setCollabReady] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);

    // Stable room name per section (requires valid real section id)
    const isValidSection = sectionId && sectionId !== 'default' && String(sectionId).trim() !== '';
    const roomName = enabled && rfpId && isValidSection ? `rfp-${rfpId}-section-${sectionId}` : null;

    useEffect(() => {
        // 1. ALWAYS clean up any existing provider and Y.Doc before establishing new one or disabling
        if (providerRef.current) {
            try {
                providerRef.current.destroy();
            } catch (e) {
                console.warn('[Collaboration] Error destroying provider:', e);
            }
            providerRef.current = null;
        }
        if (ydocRef.current) {
            try {
                ydocRef.current.destroy();
            } catch (e) {
                console.warn('[Collaboration] Error destroying ydoc:', e);
            }
            ydocRef.current = null;
        }

        hasSyncedRef.current = false;
        setYdoc(null);
        setProvider(null);
        setCollabReady(false);
        setCollaborators([]);

        // If not enabled or no roomName or no wsBaseUrl, skip setup entirely → non-collab / read-only mode
        if (!enabled || !roomName || !wsBaseUrl || !user) {
            setStatus('unavailable');
            return;
        }

        const initialToken = readToken();
        if (!initialToken) {
            console.warn('[Collaboration] No access token available. Skipping WS connection.');
            setStatus('unavailable');
            return;
        }

        let destroyed = false;
        setStatus('connecting');

        try {
            const ydocInstance = new Y.Doc();
            ydocRef.current = ydocInstance;
            setYdoc(ydocInstance);

            // Clean host base from wsBaseUrl
            let base = wsBaseUrl.trim();
            base = base.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'wss://');
            base = base.replace(/\/ws\/collab\/?$/i, '').replace(/\/+$/, '');

            const serverUrl = `${base}/ws/collab/rfp/${rfpId}/section`;
            const finalRoomName = String(sectionId);

            console.log(`[Collaboration] Connecting to: ${serverUrl}/${finalRoomName}`);

            const providerInstance = new WebsocketProvider(serverUrl, finalRoomName, ydocInstance, {
                connect: true,
                maxBackoffTime: 8000,
                params: {
                    token: initialToken,
                },
            });
            providerRef.current = providerInstance;
            setProvider(providerInstance);

            // Clean up name/email to ensure we show a friendly name, never raw email
            const getCleanName = () => {
                const rawName = user.employee_name || user.full_name || user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
                if (rawName) return rawName;

                const email = user.email || 'Anonymous';
                if (email.includes('@')) {
                    const part = email.split('@')[0];
                    return part
                        .split(/[._-]/)
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                }
                return email;
            };

            const finalCollabName = getCleanName();
            const userMeta = {
                name: finalCollabName,
                userId: String(user.id || user.email || Math.random()),
                color: getUserColor(String(user.id || user.email || '')),
                initials: getUserInitials(finalCollabName),
            };
            providerInstance.awareness.setLocalStateField('user', userMeta);
            providerInstance.awareness.setLocalStateField('isEditing', !!isEditing);

            /** Stop reconnecting, but keep the Y.Doc bound to the editor. */
            const stopReconnecting = () => {
                providerInstance.shouldConnect = false;
                try { providerInstance.disconnect(); } catch (e) { /* ignore */ }
            };

            /**
             * Give up collaboration. Only called BEFORE the first sync, so no
             * shared state exists that could be lost by switching to standard mode.
             */
            const fallbackToStandardEditing = (reason) => {
                console.warn(`[Collaboration] ${reason} Falling back to standard editing.`);
                stopReconnecting();
                setStatus('unavailable');
                setCollabReady(false);
            };

            // ── Connection status ─────────────────────────────────────────────
            providerInstance.on('status', ({ status: wsStatus }) => {
                if (destroyed) return;
                console.log(`[Collaboration] Status change: ${wsStatus}`);
                if (wsStatus === 'connected') {
                    setStatus('connected');
                } else if (wsStatus === 'disconnected') {
                    // Keep collabReady: local Y.Doc still holds everything and will re-sync.
                    setStatus((prev) => (prev === 'locked' || prev === 'error' || prev === 'unavailable' ? prev : 'disconnected'));
                } else {
                    setStatus((prev) => (prev === 'locked' || prev === 'error' || prev === 'unavailable' ? prev : 'connecting'));
                }
            });

            // ── Initial-connect watchdog (never-synced only) ──────────────────
            const initialSyncTimer = setTimeout(() => {
                if (destroyed || hasSyncedRef.current || !providerInstance.shouldConnect) return;
                fallbackToStandardEditing(`Could not sync within ${INITIAL_SYNC_TIMEOUT_MS / 1000}s.`);
            }, INITIAL_SYNC_TIMEOUT_MS);

            // ── Sync status ──────────────────────────────────────────────────
            providerInstance.on('sync', (isSynced) => {
                if (destroyed) return;
                console.log(`[Collaboration] Sync event: ${isSynced}`);
                if (isSynced) {
                    hasSyncedRef.current = true;
                    clearTimeout(initialSyncTimer);
                    setCollabReady(true);
                    setStatus('connected');
                }
            });

            // ── Close / Error events ──────────────────────────────────────────
            providerInstance.on('connection-close', (event) => {
                if (destroyed) return;
                const code = event?.code;
                const reason = typeof event?.reason === 'string' ? event.reason : '';
                console.warn('[Collaboration WS] Connection closed:', code, reason);

                // Section was submitted and is now locked for everyone in the room.
                if (code === LOCKED_CLOSE_CODE) {
                    clearTimeout(initialSyncTimer);
                    stopReconnecting();
                    setStatus('locked');
                    return;
                }

                // Pick up a fresh token so the next reconnect attempt can succeed
                // if the token was refreshed elsewhere (y-websocket rebuilds the URL from params).
                const freshToken = readToken();
                if (freshToken && providerInstance.params) {
                    providerInstance.params.token = freshToken;
                }

                const isAuthFailure = AUTH_CLOSE_CODES.includes(code);
                const isUnsupported = UNSUPPORTED_CLOSE_CODES.includes(code) || reason.toLowerCase().includes('pycrdt');

                if (isAuthFailure || isUnsupported) {
                    clearTimeout(initialSyncTimer);
                    if (hasSyncedRef.current) {
                        // Keep the document: user can keep editing locally and press Retry.
                        console.warn(`[Collaboration WS] Server refused reconnect (${code}: ${reason || 'n/a'}). Keeping local document.`);
                        stopReconnecting();
                        setStatus('error');
                    } else {
                        fallbackToStandardEditing(`Server refused connection (${code}: ${reason || 'unsupported'}).`);
                    }
                    return;
                }

                if (!hasSyncedRef.current && providerInstance.wsUnsuccessfulReconnects >= MAX_INITIAL_FAILURES) {
                    clearTimeout(initialSyncTimer);
                    fallbackToStandardEditing('Max initial connection attempts reached.');
                    return;
                }

                // Transient drop: y-websocket keeps retrying with backoff.
                setStatus((prev) => (prev === 'locked' || prev === 'error' || prev === 'unavailable' ? prev : 'disconnected'));
            });

            providerInstance.on('connection-error', (event) => {
                if (destroyed || !providerInstance.shouldConnect) return;
                console.warn('[Collaboration WS] Connection error:', event);
            });

            // Reconnect promptly when the browser comes back online / the tab is shown again.
            const nudgeReconnect = () => {
                if (destroyed) return;
                if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
                if (providerInstance.shouldConnect && !providerInstance.wsconnected && !providerInstance.wsconnecting) {
                    providerInstance.connect();
                }
            };
            window.addEventListener('online', nudgeReconnect);
            document.addEventListener('visibilitychange', nudgeReconnect);

            // Immediate state check
            if (providerInstance.wsconnected) {
                setStatus('connected');
            }
            if (providerInstance.synced) {
                hasSyncedRef.current = true;
                clearTimeout(initialSyncTimer);
                setCollabReady(true);
            }

            // ── Awareness (presence) updates ──────────────────────────────────
            const updateCollaborators = () => {
                if (destroyed) return;
                const states = Array.from(providerInstance.awareness.getStates().entries());
                const peers = states
                    .filter(([clientId, state]) => state?.user && clientId !== providerInstance.awareness.clientID && state.isEditing === true)
                    .map(([clientId, state]) => ({ clientId, ...state.user }));
                setCollaborators(peers);
            };

            providerInstance.awareness.on('change', updateCollaborators);
            updateCollaborators(); // initial read

            return () => {
                destroyed = true;
                clearTimeout(initialSyncTimer);
                window.removeEventListener('online', nudgeReconnect);
                document.removeEventListener('visibilitychange', nudgeReconnect);
                try {
                    providerInstance.awareness.off('change', updateCollaborators);
                } catch (e) { /* ignore */ }
                try {
                    providerInstance.destroy();
                } catch (e) { /* ignore */ }
                try {
                    ydocInstance.destroy();
                } catch (e) { /* ignore */ }
                ydocRef.current = null;
                providerRef.current = null;
                hasSyncedRef.current = false;
                setYdoc(null);
                setProvider(null);
                setCollabReady(false);
                setStatus('disconnected');
                setCollaborators([]);
            };
        } catch (err) {
            console.warn('[Collaboration] Setup failed – falling back to standard editing mode.', err);
            setStatus('unavailable');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomName, wsBaseUrl, user?.id, enabled, retryNonce]);

    useEffect(() => {
        const p = providerRef.current;
        if (p?.awareness) {
            p.awareness.setLocalStateField('isEditing', !!isEditing);
        }
    }, [isEditing]);

    /**
     * Manual retry.
     *  - Stopped after a successful sync ('error'): reconnect the SAME provider so the
     *    local Y.Doc (with any unsynced edits) is preserved and merged.
     *  - Never synced ('unavailable'): rebuild the session from scratch.
     */
    const retry = useCallback(() => {
        const p = providerRef.current;
        if (p && hasSyncedRef.current) {
            const freshToken = readToken();
            if (freshToken && p.params) p.params.token = freshToken;
            p.shouldConnect = true;
            p.wsUnsuccessfulReconnects = 0;
            setStatus('connecting');
            p.connect();
        } else {
            setRetryNonce((n) => n + 1);
        }
    }, []);

    return {
        ydoc,               // Y.Doc instance (null until connected)
        provider,           // WebsocketProvider instance (null until connected)
        status,             // see header comment
        collaborators,      // array of { clientId, name, color, initials, userId }
        collabReady,        // true once synced at least once for this room
        retry,              // manual reconnect
    };
}
